"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CarouselSectionData, CarouselSlideData, SectionProps } from "@/themes/default/templates/section/types";
import { SlideLayers } from "@/themes/default/templates/section/_shared/SlideLayers";
import {
  heroSpacingStyle,
  heroResponsiveClasses,
  heroContainerStyle,
} from "@/themes/default/templates/section/hero/hero-controls";

const heightMap: Record<string, string> = {
  small: "min-h-[320px]",
  medium: "min-h-[480px]",
  large: "min-h-[600px]",
  // fullscreen height is applied inline (viewport minus the sticky header)
  // so it never overflows past the fold — see the section style below.
  fullscreen: "",
};

/**
 * ci926 fork of the default Carousel Section — identical slide rendering,
 * with the theme's own bottom control bar (counter, caption, arrows and a
 * gold autoplay-progress track) instead of side arrows + centre dots.
 *
 * Carousel Section — multi-slide hero/banner where each slide can carry
 * any combination of background image/video, foreground image, title,
 * subtitle, description and buttons. Per-slide layout (`centered` /
 * `split-left` / `split-right` / `flat`) decides how those parts are
 * arranged. Empty fields are omitted, so the same slide template
 * doubles as a welcome banner, rector portrait, history block etc.
 *
 * `backgroundVideo` takes priority over `backgroundImage` when both
 * are set on the same slide.
 */
export function CarouselSection({
  section,
  currentLanguage = "en",
  sectionNodes,
}: SectionProps<CarouselSectionData> & {
  /** Pre-rendered nodes for type 'section' layers, keyed by layer id. */
  sectionNodes?: Record<string, React.ReactNode>;
}) {
  const slides = section.slides || [];
  const [active, setActive] = useState(0);

  const autoplay = section.autoplay ?? true;
  const autoplaySpeed = section.autoplaySpeed ?? 8000;
  const transitionEffect = section.transitionEffect ?? "fade";
  const transitionDuration = section.transitionDuration ?? 600;

  // Generic editor controls (fallback-safe).
  const sectionStyle = heroSpacingStyle(section as any);
  const responsive = heroResponsiveClasses(section as any);
  const containerStyle = heroContainerStyle(section as any);

  // Per-slide transition styling. `slide` translates a horizontal track,
  // `zoom` scales in, `fade`/`none` cross-fade. (Previously every effect
  // only toggled opacity, so slide/zoom hard-cut instead of animating.)
  const slideLayer = (
    idx: number,
  ): { className: string; style: React.CSSProperties } => {
    const isActive = idx === active;
    const dur = transitionEffect === "none" ? 0 : transitionDuration;
    const base = "absolute inset-0 transition-all ease-in-out";
    if (transitionEffect === "slide") {
      return {
        className: `${base} ${isActive ? "z-10" : "z-0"}`,
        style: {
          transitionDuration: `${dur}ms`,
          transform: `translateX(${(idx - active) * 100}%)`,
        },
      };
    }
    if (transitionEffect === "zoom") {
      return {
        className: `${base} ${
          isActive ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
        }`,
        style: {
          transitionDuration: `${dur}ms`,
          transform: isActive ? "scale(1)" : "scale(1.08)",
        },
      };
    }
    // fade / none
    return {
      className: `${base} ${
        isActive ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
      }`,
      style: { transitionDuration: `${dur}ms` },
    };
  };

  // Auto-advance
  useEffect(() => {
    if (!autoplay || slides.length <= 1) return;
    const interval = setInterval(
      () => setActive((p) => (p + 1) % slides.length),
      autoplaySpeed,
    );
    return () => clearInterval(interval);
  }, [autoplay, autoplaySpeed, slides.length]);

  if (slides.length === 0) {
    return null;
  }

  return (
    <section
      className={`relative w-full overflow-hidden ${heightMap[section.height || "large"]} ${responsive}`}
      style={{
        ...sectionStyle,
        // Fullscreen = viewport height minus the sticky header (published as
        // --site-header-h by HeaderHeightVar), so the slider fills the screen
        // without pushing content below the fold.
        ...((section.height || "large") === "fullscreen"
          ? { minHeight: "calc(100dvh - var(--site-header-h, 130px))" }
          : {}),
      }}
      data-section-id={section._id}
      data-section-type={section.type}
    >
      {slides.map((slide, idx) => {
        const layer = slideLayer(idx);
        return (
          <div
            key={slide.id || idx}
            className={layer.className}
            style={layer.style}
            aria-hidden={idx !== active || undefined}
          >
            <SlideRenderer
              slide={slide}
              language={currentLanguage}
              containerStyle={containerStyle}
            />
            {/* Free-positioned content layers (over the built-in content).
                z-20 keeps arrows/dots (z-30) clickable. */}
            {slide.layers && slide.layers.length > 0 && (
              <div className="absolute inset-0 z-20">
                <div className="relative mx-auto h-full w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                  <SlideLayers
                    layers={slide.layers}
                    active={idx === active}
                    currentLanguage={currentLanguage}
                    slideKey={active}
                    sectionNodes={sectionNodes}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ci926 slide controls — one bar along the bottom, aligned to the
          content container: counter + caption (left), prev/next (right),
          and a segmented progress track whose active segment fills gold
          over the autoplay interval. Replaces the side arrows + centre dots. */}
      {slides.length > 1 && ((section.showDots ?? true) || (section.showArrows ?? true)) && (
        <div className="absolute inset-x-0 bottom-0 z-30 pointer-events-none">
          {/* Below lg the floating mobile bottom-nav overlays the hero foot —
              lift the controls clear of it. */}
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-28 lg:pb-7">
            <div className="flex items-end justify-between gap-6">
              {(section.showDots ?? true) ? (
                <div className="pointer-events-auto flex items-baseline gap-3 text-white min-w-0" aria-live="polite">
                  <span className="ci926-stat-number text-3xl sm:text-4xl tabular-nums">
                    {String(active + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm tabular-nums text-white/55">
                    / {String(slides.length).padStart(2, "0")}
                  </span>
                  {slideCaption(slides[active], currentLanguage) && (
                    <span className="hidden sm:block truncate max-w-[28ch] border-l border-white/25 pl-3 text-xs uppercase tracking-[0.16em] text-white/75">
                      {slideCaption(slides[active], currentLanguage)}
                    </span>
                  )}
                </div>
              ) : (
                <span />
              )}
              {(section.showArrows ?? true) && (
                <div className="pointer-events-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActive((p) => (p - 1 + slides.length) % slides.length)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 text-white transition-colors hover:border-[color:var(--color-accent)] hover:bg-[color:var(--color-accent)] hover:text-[color:var(--color-accent-foreground)]"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActive((p) => (p + 1) % slides.length)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 text-white transition-colors hover:border-[color:var(--color-accent)] hover:bg-[color:var(--color-accent)] hover:text-[color:var(--color-accent-foreground)]"
                    aria-label="Next slide"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
            {(section.showDots ?? true) && (
              <div className="pointer-events-auto mt-4 flex gap-1.5">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    className="group relative h-6 flex-1 cursor-pointer"
                    aria-label={`Go to slide ${i + 1}`}
                    aria-current={i === active ? "true" : undefined}
                  >
                    <span className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-white/25 transition-colors group-hover:bg-white/45">
                      <span
                        key={i === active ? `on-${active}` : "off"}
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={
                          i === active
                            ? {
                                width: "100%",
                                background: "var(--color-accent)",
                                transformOrigin: "left",
                                animation: autoplay
                                  ? `ci926-slide-progress ${autoplaySpeed}ms linear forwards`
                                  : undefined,
                              }
                            : { width: i < active ? "100%" : "0%", background: "rgba(255,255,255,0.7)" }
                        }
                      />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/** Short caption for the counter: the slide's title (first "|" segment). */
function slideCaption(slide: CarouselSlideData | undefined, lang: "en" | "mm"): string {
  const t = (slide as any)?.title;
  const text = typeof t === "string" ? t : (t?.[lang] || t?.en || t?.mm || "");
  return String(text).split("|")[0].trim();
}

/** `#rrggbb` / `#rgb` + alpha → `rgba(...)` (falls back to black). */
function rgba(hex: string, alpha: number): string {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = /^[0-9a-f]{6}$/i.test(h) ? parseInt(h, 16) : 0;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/** Vignette layers: full `strength` behind the centred text, ~1/3 at the
 *  edges, and a short bottom floor for the control bar. */
function vignette(color: string, strength: number): string {
  const s = Math.max(0, Math.min(1, strength));
  return [
    `linear-gradient(to top, ${rgba(color, Math.max(s, 0.5))} 0%, ${rgba(color, 0)} 26%)`,
    `radial-gradient(ellipse 62% 55% at 50% 50%, ${rgba(color, s)} 0%, ${rgba(color, s * 0.7)} 45%, ${rgba(color, s * 0.32)} 100%)`,
  ].join(", ");
}

interface SlideRendererProps {
  slide: CarouselSlideData;
  language: "en" | "mm";
  containerStyle?: React.CSSProperties;
}

function SlideRenderer({ slide, language, containerStyle }: SlideRendererProps) {
  const title = slide.title?.[language] || slide.title?.en;
  const subtitle = slide.subtitle?.[language] || slide.subtitle?.en;
  const description =
    slide.description?.[language] || slide.description?.en;
  const buttons = slide.buttons || [];
  const layout = slide.layout || "centered";
  const contentStyle = slide.contentStyle || "flat";
  const textAlignment = slide.textAlignment || "center";

  const showForegroundImage = layout !== "flat" && !!slide.image;
  const isSplit = layout === "split-left" || layout === "split-right";

  const buttonClass = (style: "primary" | "secondary" | "outline") => {
    if (style === "secondary") {
      return "bg-white/90 text-foreground hover:bg-white";
    }
    if (style === "outline") {
      return "border border-white text-white hover:bg-white hover:text-foreground";
    }
    return "bg-primary text-primary-foreground hover:bg-primary/90";
  };

  const textAlignClass =
    textAlignment === "left"
      ? "text-left items-start"
      : textAlignment === "right"
      ? "text-right items-end"
      : "text-center items-center";

  const contentBoxClass =
    contentStyle === "boxed"
      ? "bg-black/40 backdrop-blur-sm rounded-lg p-6 md:p-8"
      : "";

  const justifyClass =
    textAlignment === "left"
      ? "justify-start"
      : textAlignment === "right"
      ? "justify-end"
      : "justify-center";

  const renderTextContent = () => (
    <div
      className={`relative z-10 flex flex-col gap-4 max-w-3xl ${textAlignClass} ${contentBoxClass}`}
    >
      {showForegroundImage && layout === "centered" && slide.image && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={slide.image}
          alt=""
          className="h-24 md:h-28 w-auto object-contain"
        />
      )}
      {/* Subtitle as a badge: a translucent ink pill with a gold dot, so the
          small caps label stays readable on bright photos. */}
      {subtitle && (
        <p
          className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm"
          style={{ backgroundColor: "rgba(15, 24, 43, 0.55)" }}
        >
          <span
            aria-hidden
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: "var(--color-accent)" }}
          />
          {subtitle}
        </p>
      )}
      {title && (
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
          {title}
        </h2>
      )}
      {description && (
        <p className="text-base md:text-lg text-white/90 leading-relaxed">
          {description}
        </p>
      )}
      {buttons.length > 0 && (
        <div className={`flex flex-wrap gap-3 pt-3 ${justifyClass}`}>
          {buttons.map((b, i) => {
            const buttonText =
              b.text?.[language] || b.text?.en || "Button";
            return (
              <Link
                key={i}
                href={b.url || "#"}
                target={b.openInNewTab ? "_blank" : undefined}
                rel={b.openInNewTab ? "noopener noreferrer" : undefined}
                className={`px-5 py-2.5 rounded text-sm md:text-base font-medium transition-colors ${buttonClass(b.style)}`}
              >
                {buttonText}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="absolute inset-0">
      {/* Background — video wins over image when both set */}
      {slide.backgroundVideo ? (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={slide.backgroundVideo} />
        </video>
      ) : slide.backgroundImage ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${slide.backgroundImage})` }}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900" />
      )}

      {/* Overlay — a soft vignette instead of a flat wash: `overlay.opacity`
          is the strength behind the (centred) text, fading to ~a third of it
          at the edges so the photo stays bright, plus a light floor so the
          bottom control bar stays legible. */}
      {slide.overlay?.enabled && (
        <div
          className="absolute inset-0"
          style={{ backgroundImage: vignette(slide.overlay.color || "#000000", slide.overlay.opacity ?? 0.4) }}
        />
      )}

      {/* Foreground content */}
      <div className="relative z-10 h-full px-6 md:px-12 lg:px-20 py-12 md:py-20 flex items-center">
        {isSplit ? (
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center w-full max-w-7xl mx-auto ${
              layout === "split-right" ? "md:[&>*:first-child]:order-2" : ""
            }`}
            style={containerStyle}
          >
            <div className="flex justify-center md:justify-start">
              {showForegroundImage && slide.image && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={slide.image}
                  alt=""
                  className="max-h-96 w-auto object-contain rounded shadow-2xl"
                />
              )}
            </div>
            <div>{renderTextContent()}</div>
          </div>
        ) : (
          <div
            className={`w-full max-w-7xl mx-auto flex ${justifyClass}`}
            style={containerStyle}
          >
            {renderTextContent()}
          </div>
        )}
      </div>
    </div>
  );
}

export default CarouselSection;
